import React, { FunctionComponent, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Custom Components
import model from '@/models/zalbe';
import Permissions from '@/lib/permissions';

import dataController, { IDataController } from '@/lib/DataController';
import ModelTable from '@/ui/Table/ModelTable';
import TableButtonEdit from '@/ui/Table/ActionButtons/TableButtonEdit';
import DialogContext, { DialogContextType } from '@/context/DialogContext/DialogContext';
import LoaderContext, { LoaderContextType } from '@/context/LoaderContext/LoaderContext';
import GeneralDialog from '@/ui/Dialog/GeneralDialog';
import useDocuments from '@/components/useDocuments';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { ClosingDetails } from '@/@types/components/formController';
import TableButtonDocuments from '@/ui/Table/ActionButtons/TableButtonDocuments';
import UserContext, { UserContextType } from '@/context/UserContext/UserContext';


const ZalbeTable: FunctionComponent = () => {
  const userContext = useContext(UserContext);
  const { showDialog } = useContext<DialogContextType>(DialogContext);
  const loaderContext = useContext(LoaderContext) as LoaderContextType;
  const { showDocumentsDialog } = useDocuments();

  const { t } = useTranslation();

  const [records, setRecords] = useState<DCRecord[]>([]);

  const dc: IDataController = new dataController(model);

  useEffect(() => {
    refreshRecords();
  }, []);

  const handleAdd = () => {
    showDialog(GeneralDialog, {
      dc,
      mode: 'insert',
      form: 'insert',
      columns: 1,
      title: t('ena:titles.zalba_new'),
      onClose: handleDialogClose,
    });
  };

  const handleEdit = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const record = sel[Object.keys(sel)[0]];

    showDialog(GeneralDialog, {
      dc,
      mode: 'update',
      form: 'update',
      initialRecord: record,
      columns: 1,
      title: t('ena:titles.zalba'),
      onClose: handleDialogClose
    });
  };

  const handleDocuments = (evt: React.SyntheticEvent, sel: { [key: string]: DCRecord }) => {
    const selId = parseInt(Object.keys(sel)[0], 10);
    const record: DCRecord = sel[selId];
    const gid = record.gid as string;

    if (gid) {
      showDocumentsDialog(gid, handleDialogClose);
    }
  };

  const handleDialogClose = (result: ClosingDetails) => {
    switch (result.action) {
      case 'insert':
      case 'update':
      case 'delete':
        if (result.dataChanged) refreshRecords(); // only refresh if something changed
        break;
      case 'cancel': // if user canceled don't refresh
      default:
        break;
    }
  }

  const refreshRecords = () => {
    loaderContext.toggleLoading(true);
    dc.GetData()
      .then((resp) => {
        if (resp.success) {
          if (Array.isArray(resp.data)) {
            setRecords(resp.data);
          } else {
            setRecords([]);
          }
        }
      })
      .finally(() => {
        loaderContext.toggleLoading(false);
      });
  };

  const isDocumentsButtonEnabled: (selection: { [key: string]: DCRecord }) => boolean = (sel) => {
      const selId = parseInt(Object.keys(sel)[0], 10);
      const record: DCRecord = sel[selId];
  
      return !!record.gid && !record.is_deleted;
    };

  const canManage = userContext?.hasAnyPermission([Permissions.EnaManageNekretnine]);

  return (
    <ModelTable
      title={t('ena:page.zalbe')}
      records={records}
      dc={dc}
      allowSelection="one"
      allowFilter
      allowExport
      allowAdd={canManage}
      handleAdd={handleAdd}
      allowColumnPicker
      addLabel={t('buttons.new_f') as string}
      onRowAction={() => { }}
      allowRowAction={() => true}
      unselectRow={false}
      onDoubleClick={canManage ? handleEdit : () => {}}

    >
      <TableButtonDocuments isEnabledRecord={isDocumentsButtonEnabled} onClick={handleDocuments} needPermission={[Permissions.EnaManageNekretnine]} />
      <TableButtonEdit variant="contained" onClick={handleEdit} needPermission={[Permissions.EnaManageNekretnine]}/>
    </ModelTable>
  );
}

export default ZalbeTable;